import { useState, useEffect, useCallback, useRef } from 'react';
import { AuthUser, UserMealTimeConfig, MealItem } from '../types';
import { initialUserProfile, initialRecipes, initialIngredients, initialUserInventory, initialScheduleByDate, initialShoppingList } from '@foodbook/core-logic';
import { dataService } from '@foodbook/core-logic';
import { useSchedule } from './useSchedule';
import { useRecipes } from './useRecipes';
import { useIngredients } from './useIngredients';
import { useShopping } from './useShopping';
import { useUser } from './useUser';
import { useNavigation } from './useNavigation';
import { useUI } from './useUI';

export const useAppData = (authUser?: AuthUser | null, authReady: boolean = true) => {
  // Navigation Hook
  const { history, currentRoute, navigate, goBack, setHistory } = useNavigation();

  // UI Hook
  const {
    showXP,
    xpAmount,
    showToast,
    toastMessage,
    showPlanModal,
    setShowPlanModal,
    selectedRecipeForPlan,
    setSelectedRecipeForPlan,
    showToastMessage,
    awardXP: awardXPBase,
    handleAddToPlanStart: handleAddToPlanStartBase,
  } = useUI();

  // User Hook with auto-save
  const {
    user,
    setUser,
    inventory,
    setInventory,
    logs,
    setLogs,
    handleProfileUpdate,
    handleToggleTheme,
    handleSaveBatch,
    setFavoriteRecipeIds,
    setFavoriteIngredientIds,
    updateNutritionGoals,
    updateBodyData,
    updateDietaryPreferences,
    updateBasicInfo,
  } = useUser({
    initialUser: initialUserProfile,
    initialInventory: initialUserInventory,
    onUserChange: useCallback((newUser) => {
      dataService.saveUserProfile(newUser);
    }, []),
    onInventoryChange: useCallback((newInventory) => {
      dataService.saveUserInventory(newInventory);
    }, []),
  });

  // Custom Meal Times State
  const [customMealTimes, setCustomMealTimes] = useState<UserMealTimeConfig[]>([]);

  // Schedule Hook with auto-save
  // 使用空对象作为初始值，避免显示示例数据，等待从数据库加载
  const {
    scheduleByDate,
    setScheduleByDate,
    selectedDate,
    setSelectedDate,
    currentSchedule,
    handleMealComplete,
    handleAddDishStart,
    handleAddDishConfirm: handleAddDishConfirmBase,
    handleAddToPlanConfirm: handleAddToPlanConfirmBase,
    addDishTargetMeal,
    setAddDishTargetMeal,
    // 新增功能
    copySchedule,
    deleteSchedule,
    deleteMealItem,
    updateMealItem,
    calculateStats,
    getDayNutrition,
    generateShoppingListFromSchedule,
  } = useSchedule({
    initialSchedule: {},
    customMealTimes,
    onScheduleChange: useCallback((newSchedule) => {
      dataService.saveScheduleByDate(newSchedule);
    }, []),
  });

  // Recipes Hook with auto-save
  const {
    recipes,
    setRecipes,
    handleToggleFavorite: handleToggleFavoriteBase,
    handleRecipeSelect: handleRecipeSelectBase,
  } = useRecipes({
      initialRecipes: initialRecipes,
    });

  // Ingredients Hook
  const {
    ingredients,
    setIngredients,
    handleToggleIngredientFavorite: handleToggleIngredientFavoriteBase,
    handleFindRecipe,
  } = useIngredients({
    initialIngredients: initialIngredients,
  });

  // Shopping Hook with auto-save
  const {
    shoppingList,
    setShoppingList,
    handleAddToShoppingList: handleAddToShoppingListBase,
    handleCheckout: handleCheckoutBase,
  } = useShopping({
    initialShoppingList: initialShoppingList,
    onShoppingListChange: useCallback((newShoppingList) => {
      dataService.saveShoppingList(newShoppingList);
    }, []),
  });

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const loadRequestRef = useRef(0);

  // Load data on auth change
  useEffect(() => {
    if (!authReady) {
      return;
    }

    const requestId = ++loadRequestRef.current;
    let active = true;
    const isCurrent = () => active && loadRequestRef.current === requestId;

    const loadData = async () => {
      setIsLoading(true);
      const currentUserId = authUser?.uid || 'anonymous';

      setScheduleByDate({});
      setShoppingList(initialShoppingList);
      setInventory(initialUserInventory);
      setCustomMealTimes([]);
      setLogs([]);
      setFavoriteRecipeIds([]);
      setFavoriteIngredientIds([]);

      try {
        dataService.setUserId(currentUserId);

        const publicData = await dataService.loadPublicData();
        if (!isCurrent()) {
          return;
        }
        setRecipes(publicData.recipes);
        setIngredients(publicData.ingredients);

        const [
          mergedRecipesResult,
          userProfileResult,
          scheduleResult,
          shoppingResult,
          inventoryResult,
          favoritesResult,
          mealTimeConfigsResult,
          logsResult,
        ] = await Promise.allSettled([
          dataService.getAllRecipes(),
          dataService.getUserProfile(),
          dataService.getScheduleByDate(),
          dataService.getShoppingList(),
          dataService.getUserInventory(),
          dataService.getUserFavorites(),
          dataService.getUserMealTimeConfigs(),
          authUser ? dataService.getUserLogs(currentUserId) : Promise.resolve([]),
        ]);

        if (!isCurrent()) {
          return;
        }

        if (mergedRecipesResult.status === 'fulfilled') {
          setRecipes(mergedRecipesResult.value);
        }

        const baseProfile =
          userProfileResult.status === 'fulfilled'
            ? userProfileResult.value
            : { ...initialUserProfile, id: currentUserId };

        const resolvedProfile = authUser
          ? {
              ...baseProfile,
              id: authUser.uid,
              name: authUser.nickname || authUser.email || baseProfile.name,
              email: authUser.email || baseProfile.email,
            }
          : baseProfile;

        setUser(resolvedProfile);

        if (authUser) {
          void dataService.saveUserProfile(resolvedProfile).catch((error) => {
            console.error('[useAppData] Failed to sync resolved user profile:', error);
          });
        }

        if (scheduleResult.status === 'fulfilled') {
          setScheduleByDate(scheduleResult.value);
          console.log('[useAppData] Loaded scheduleByDate:', scheduleResult.value);
          console.log('[useAppData] Number of dates in loaded schedule:', Object.keys(scheduleResult.value).length);
        } else {
          setScheduleByDate({});
        }

        if (shoppingResult.status === 'fulfilled') {
          setShoppingList(shoppingResult.value);
        } else {
          setShoppingList(initialShoppingList);
        }

        if (inventoryResult.status === 'fulfilled') {
          setInventory(inventoryResult.value);
        } else {
          setInventory(initialUserInventory);
        }

        if (mealTimeConfigsResult.status === 'fulfilled') {
          setCustomMealTimes(mealTimeConfigsResult.value);
        } else {
          setCustomMealTimes([]);
        }

        if (favoritesResult.status === 'fulfilled') {
          const favoriteRecipeIds = favoritesResult.value
            .filter(f => f.type === 'recipe')
            .map(f => f.itemId);
          const favoriteIngredientIds = favoritesResult.value
            .filter(f => f.type === 'ingredient')
            .map(f => f.itemId);

          setFavoriteRecipeIds(favoriteRecipeIds);
          setFavoriteIngredientIds(favoriteIngredientIds);
        } else {
          setFavoriteRecipeIds([]);
          setFavoriteIngredientIds([]);
        }

        if (logsResult.status === 'fulfilled') {
          setLogs(logsResult.value || []);
        } else {
          setLogs([]);
        }
      } catch (error) {
        console.error('[useAppData] Failed to load data:', error);
      } finally {
        if (isCurrent()) {
          setIsLoading(false);
        }
      }
    };
    loadData();

    return () => {
      active = false;
    };
  }, [authUser?.uid, authReady]);

  // Award XP wrapper
  const awardXP = useCallback((amount: number) => {
    awardXPBase(amount, (amt) => {
      setUser(prev => {
        const newXp = prev.xp + amt;
        if (newXp >= prev.maxXp) {
          return { ...prev, level: prev.level + 1, xp: newXp - prev.maxXp, maxXp: Math.round(prev.maxXp * 1.2) };
        }
        return { ...prev, xp: newXp };
      });
    });
  }, [awardXPBase, setUser]);

  // Handle Add Dish Start wrapper - 包装 handleAddDishStart，绑定 navigate
  const handleAddDishStartWrapped = useCallback((mealTitle: string) => {
    handleAddDishStart(mealTitle, navigate);
  }, [handleAddDishStart, navigate]);

  // Handle Add Dish Confirm wrapper
  const handleAddDishConfirm = useCallback((recipe: any) => {
    handleAddDishConfirmBase(recipe, addDishTargetMeal, currentSchedule, goBack, showToastMessage, awardXP);
  }, [handleAddDishConfirmBase, addDishTargetMeal, currentSchedule, goBack, showToastMessage, awardXP]);

  // Handle Add To Plan Confirm wrapper
  const handleAddToPlanConfirm = useCallback((date: string, meal: string, options?: { quantity?: number; note?: string }) => {
    handleAddToPlanConfirmBase(date, meal, selectedRecipeForPlan, options, showToastMessage, awardXP);
    setShowPlanModal(false);
  }, [handleAddToPlanConfirmBase, selectedRecipeForPlan, showToastMessage, awardXP, setShowPlanModal]);

  // 批量操作包装
  const handleCopySchedule = useCallback((sourceDate: string, targetDate: string) => {
    copySchedule(sourceDate, targetDate);
    showToastMessage(`已将 ${sourceDate} 的计划复制到 ${targetDate}`);
  }, [copySchedule, showToastMessage]);

  const handleDeleteSchedule = useCallback((date: string) => {
    deleteSchedule(date);
    showToastMessage(`已清空 ${date} 的计划`);
  }, [deleteSchedule, showToastMessage]);

  const handleDeleteMealItem = useCallback((mealTitle: string, itemId: string) => {
    deleteMealItem(selectedDate, mealTitle, itemId);
    showToastMessage('已删除计划项');
  }, [deleteMealItem, selectedDate, showToastMessage]);

  const handleUpdateMealItem = useCallback((mealTitle: string, itemId: string, updates: Partial<MealItem>) => {
    updateMealItem(selectedDate, mealTitle, itemId, updates);
  }, [updateMealItem, selectedDate]);

  // 从计划生成购物清单
  const handleGenerateShoppingListFromSchedule = useCallback((date: string) => {
    const items = generateShoppingListFromSchedule([date]);
    if (items.length === 0) {
      showToastMessage('该日期暂无计划，无法生成购物清单');
      return;
    }

    // 转换为购物清单项格式
    const shoppingItems = items.map((item, idx) => ({
      id: `schedule-${Date.now()}-${idx}`,
      name: item.name,
      quantity: item.amount,
      checked: false,
      date: selectedDate,
      linkedIngredientId: item.linkedIngredientId,
    }));

    // 添加到现有购物清单
    setShoppingList(prev => [...prev, ...shoppingItems]);
    showToastMessage(`已添加 ${shoppingItems.length} 项到购物清单`);
  }, [generateShoppingListFromSchedule, selectedDate, showToastMessage, setShoppingList]);

  // 时段配置管理方法
  const handleSaveMealTimeConfigs = useCallback(async (configs: UserMealTimeConfig[]) => {
    await dataService.saveUserMealTimeConfigs(configs);
    setCustomMealTimes(configs);
    showToastMessage('时段配置已保存');
  }, [showToastMessage]);

  const handleAddMealTimeConfig = useCallback(async (config: Omit<UserMealTimeConfig, 'id'>) => {
    const newConfig = await dataService.addMealTimeConfig(config);
    setCustomMealTimes(prev => [...prev, newConfig]);
    showToastMessage('时段已添加');
  }, [showToastMessage]);

  const handleUpdateMealTimeConfig = useCallback(async (id: string, updates: Partial<UserMealTimeConfig>) => {
    await dataService.updateMealTimeConfig(id, updates);
    setCustomMealTimes(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
    showToastMessage('时段已更新');
  }, [showToastMessage]);

  const handleDeleteMealTimeConfig = useCallback(async (id: string) => {
    await dataService.deleteMealTimeConfig(id);
    setCustomMealTimes(prev => prev.filter(c => c.id !== id));
    showToastMessage('时段已删除');
  }, [showToastMessage]);

  // Handle Toggle Favorite wrapper
  const handleToggleFavorite = useCallback((recipeId: string) => {
    if (dataService.isAnonymous()) {
      showToastMessage('请先登录以保存收藏');
      return;
    }
    handleToggleFavoriteBase(recipeId, user.favoriteRecipeIds || [], setFavoriteRecipeIds);
  }, [handleToggleFavoriteBase, user.favoriteRecipeIds, setFavoriteRecipeIds, showToastMessage]);

  // Handle Recipe Select wrapper
  const handleRecipeSelect = useCallback((recipe: any) => {
    handleRecipeSelectBase(recipe, addDishTargetMeal, handleAddDishConfirm, navigate);
  }, [handleRecipeSelectBase, addDishTargetMeal, handleAddDishConfirm, navigate]);

  // Handle Toggle Ingredient Favorite wrapper
  const handleToggleIngredientFavorite = useCallback((ingredientId: string) => {
    if (dataService.isAnonymous()) {
      showToastMessage('请先登录以保存收藏');
      return;
    }
    handleToggleIngredientFavoriteBase(ingredientId, user.favoriteIngredientIds || [], setFavoriteIngredientIds);
  }, [handleToggleIngredientFavoriteBase, user.favoriteIngredientIds, setFavoriteIngredientIds, showToastMessage]);

  // Handle Add To Shopping List wrapper
  const handleAddToShoppingList = useCallback((
    ingredientName: string,
    options?: {
      linkedIngredientId?: string;
      quantity?: string;
      date?: string;
    }
  ) => {
    handleAddToShoppingListBase(ingredientName, selectedDate, showToastMessage, options);
  }, [handleAddToShoppingListBase, selectedDate, showToastMessage]);

  // Handle Checkout wrapper
  const handleCheckout = useCallback((items: any[]) => {
    handleCheckoutBase(items, ingredients, inventory, setInventory, showToastMessage, awardXP);
  }, [handleCheckoutBase, ingredients, inventory, setInventory, showToastMessage, awardXP]);

  // Handle Save Batch wrapper - 包装 handleSaveBatch，绑定 ingredients 和 showToastMessage
  const handleSaveBatchWrapped = useCallback((ingredientId: string, batch: any) => {
    handleSaveBatch(ingredientId, batch, ingredients, showToastMessage);
  }, [handleSaveBatch, ingredients, showToastMessage]);

  // Handle Add To Plan Start wrapper
  const handleAddToPlanStart = useCallback((recipe: any) => {
    handleAddToPlanStartBase(recipe, setShowPlanModal);
  }, [handleAddToPlanStartBase, setShowPlanModal]);

  // Handle Meal Item Click
  const handleMealItemClick = useCallback((mealItem: any) => {
    if (mealItem.recipeId) {
      navigate('recipe-detail', { recipeId: mealItem.recipeId });
    }
  }, [navigate]);

  // Reset all data
  const resetAllData = useCallback(async () => {
    await dataService.resetAll();
    setRecipes(initialRecipes);
    setIngredients(initialIngredients);
    setUser(initialUserProfile);
    setScheduleByDate(initialScheduleByDate);
    setShoppingList(initialShoppingList);
    showToastMessage('数据已重置');
  }, [setRecipes, setIngredients, setUser, setScheduleByDate, setShoppingList, showToastMessage]);

  // 计算计划统计
  const getScheduleStats = useCallback((startDate: string, endDate: string) => {
    return calculateStats(startDate, endDate);
  }, [calculateStats]);

  // 获取某天营养
  const getDayNutritionData = useCallback((date: string) => {
    return getDayNutrition(date);
  }, [getDayNutrition]);

  return {
    // Data
    scheduleByDate,
    setScheduleByDate,
    selectedDate,
    setSelectedDate,
    currentSchedule,
    customMealTimes,
    // 新增：计划统计方法
    getScheduleStats,
    getDayNutritionData,
    recipes,
    setRecipes,
    ingredients,
    setIngredients,
    shoppingList,
    setShoppingList,
    user,
    setUser,
    inventory,
    setInventory,
    logs,
    setLogs,
    isLoading,

    // Navigation
    currentRoute,
    navigate,
    goBack,

    // UI State
    showXP,
    xpAmount,
    showToast,
    toastMessage,
    showPlanModal,
    setShowPlanModal,
    selectedRecipeForPlan,
    setSelectedRecipeForPlan,

    // Handlers
    awardXP,
    showToastMessage,
    handleMealComplete,
    handleMealItemClick,
    handleAddDishStart: handleAddDishStartWrapped,
    handleAddDishConfirm,
    handleRecipeSelect,
    handleAddToPlanStart,
    handleAddToPlanConfirm,
    handleFindRecipe,
    handleCheckout,
    handleAddToShoppingList,
    handleToggleFavorite,
    handleToggleTheme,
    handleToggleIngredientFavorite,
    handleSaveBatch: handleSaveBatchWrapped,
    handleProfileUpdate,
    resetAllData,
    // 新增：计划管理功能
    handleCopySchedule,
    handleDeleteSchedule,
    handleDeleteMealItem,
    handleUpdateMealItem,
    handleGenerateShoppingListFromSchedule,
    // 新增：时段配置管理
    handleSaveMealTimeConfigs,
    handleAddMealTimeConfig,
    handleUpdateMealTimeConfig,
    handleDeleteMealTimeConfig,
    // 用户目标更新方法
    updateNutritionGoals,
    updateBodyData,
    updateDietaryPreferences,
    updateBasicInfo,
  };
};
